import {
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Text,
    Alert,
    AlertIcon,
    VStack,
    HStack,
    Link as ChakraLink,
    Button,
    Flex,
    Spinner,
    Icon,
    IconButton,

    Textarea,
    useToast,
    Portal,
} from '@chakra-ui/react';
import { TbArrowUp, TbMessageCircle, TbBuilding, TbPinnedFilled } from 'react-icons/tb';
import { FiList } from 'react-icons/fi';
import { Link as RouterLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useState, useMemo, useCallback, useContext, useEffect, useRef } from 'react';
import ResizeTextarea from 'react-textarea-autosize';
import { useUpdateOrderDetails } from '../hooks/useUpdateOrderDetails';
import { useOrderBulkSelection, handleOrderBulkRowClick } from '../hooks/useOrderBulkSelection';
import { useOrderBulkActions } from '../hooks/useOrderBulkActions';
import OrderBulkActionBar from '../components/orders/OrderBulkActionBar';
import { HOME_CONTENT_PL_WITH_BULK } from '../constants/pageLayout';
import OrderRoColumnCheckboxSlot from '../components/orders/OrderRoColumnCheckboxSlot';
import OrderSelectAllHeaderCheckbox from '../components/orders/OrderSelectAllHeaderCheckbox';
import { HomeRoColumnRow } from '../components/orders/orderRoColumnLayout';
import { getOrderBulkPermissions } from '../utils/orderBulkActions';
import { useGetMentionableUsers, MentionableUser } from '../hooks/useGetMentionableUsers';
import { positionLabel } from '../utils/invitePositionOptions';
import { note } from '../ui/theme/tokens';
import { useHomeFeedPaginated, HomeFeedFilter, AdditionalFilters } from '../hooks/useHomeFeed';

import { formatPercentage } from '../functions';
import LocationSelector from '../components/LocationSelector';
import OrderDrawer, { OrderDrawerPanelState } from '../modals/OrderDrawer';
import Pagination from '../components/Pagination';
import RefreshButton from '../components/RefreshButton';
import ActionBadge from '../components/ActionBadge';
import SessionContext from '../contexts/SessionContext';
import StatusIndicator from '../components/StatusIndicator';
import { Vin, NoteCountBadge, SupplierCommunicationCountBadge } from '../components/orders/components';
import AdvancedFilters from '../components/AdvancedFilters';
import { useTableSettings } from '../hooks/useTableSettings';
import TableSettingsButton from '../components/TableSettingsButton';
import { useTabConfig, ALL_TABS } from '../hooks/useTabConfig';
import { useColumnWidths } from '../hooks/useColumnWidths';
import { useStickyTableCopyFront } from '../hooks/useStickyTableCopyFront';
import { stickyTableScrollCopySx, STICKY_TABLE_HEADER_Z_INDEX } from '../utils/tableCopySx';
import { useScrollShadow } from '../hooks/useScrollShadow';
import { useResponsiveToolbar } from '../hooks/useResponsiveToolbar';
import usePageTitle from '../hooks/usePageTitle';
import ResizableTh from '../components/ResizableTh';
import CollapsibleSearch, { LIST_PAGE_SEARCH_WIDTH } from '../components/CollapsibleSearch';
import ListTabRow from '../components/ListTabRow';
import { areAdditionalFiltersEqual, areLocationSelectionsEqual, parseCachedAdditionalFilters } from '../utils/viewStateUtils';
import { recallListSearch } from '../shared/listSearchMemory';
import { useRememberListSearch } from '../hooks/useRememberListSearch';

const HOME_VIEW_STATE_CACHE_KEY = 'home_view_state_cache';

interface HomeViewStateCache {
    locationIds: Array<string> | 'all';
    currentPage: number;
    selectedFilter: HomeFeedFilter | null;
    searchQuery: string;
    debouncedSearch: string;
    additionalFilters: AdditionalFilters;
    selectedOrderId: string | null;
    isOrderDrawerOpen: boolean;
    orderDrawerPanelState: OrderDrawerPanelState;
}

interface HomeReturnState {
    selectedOrderId?: string | null;
    isOrderDrawerOpen?: boolean;
    panelState?: OrderDrawerPanelState;
}

const DEFAULT_ORDER_DRAWER_PANEL_STATE: OrderDrawerPanelState = {
    panel: 'none',
    supplierInitialTab: 'quoting-comments',
    supplierInitialSupplierOrderId: null,
    workflowTab: 'ems',
};

const normalizeOrderDrawerPanelState = (
    panelState?: OrderDrawerPanelState
): OrderDrawerPanelState => ({
    panel: panelState?.panel ?? DEFAULT_ORDER_DRAWER_PANEL_STATE.panel,
    supplierInitialTab: panelState?.supplierInitialTab ?? DEFAULT_ORDER_DRAWER_PANEL_STATE.supplierInitialTab,
    supplierInitialSupplierOrderId: panelState?.supplierInitialSupplierOrderId ?? DEFAULT_ORDER_DRAWER_PANEL_STATE.supplierInitialSupplierOrderId,
    workflowTab: panelState?.workflowTab ?? DEFAULT_ORDER_DRAWER_PANEL_STATE.workflowTab,
});

const loadHomeViewStateCache = (): HomeViewStateCache | null => {
    if (typeof window === 'undefined') return null;

    try {
        const saved = sessionStorage.getItem(HOME_VIEW_STATE_CACHE_KEY);
        if (!saved) return null;

        const parsed = JSON.parse(saved) as HomeViewStateCache;
        return {
            ...parsed,
            additionalFilters: parseCachedAdditionalFilters(parsed.additionalFilters ?? {}),
            orderDrawerPanelState: normalizeOrderDrawerPanelState(parsed.orderDrawerPanelState),
        };
    } catch {
        return null;
    }
};

const areOrderDrawerPanelStatesEqual = (
    a: OrderDrawerPanelState,
    b: OrderDrawerPanelState
): boolean => (
    a.panel === b.panel &&
    a.supplierInitialTab === b.supplierInitialTab &&
    a.supplierInitialSupplierOrderId === b.supplierInitialSupplierOrderId &&
    a.workflowTab === b.workflowTab
);

const Home = () => {
    const session = useContext(SessionContext);
    const location = useLocation();
    const navigate = useNavigate();
    const homeViewStateFromLocation = useMemo<HomeViewStateCache | null>(() => {
        const state = location.state as { homeViewState?: HomeViewStateCache; } | null;
        const viewState = state?.homeViewState;
        if (!viewState) return null;

        return {
            locationIds: viewState.locationIds ?? [],
            currentPage: viewState.currentPage ?? 1,
            selectedFilter: viewState.selectedFilter ?? null,
            searchQuery: viewState.searchQuery ?? '',
            debouncedSearch: viewState.debouncedSearch ?? viewState.searchQuery ?? '',
            additionalFilters: parseCachedAdditionalFilters(viewState.additionalFilters ?? {}),
            selectedOrderId: viewState.selectedOrderId ?? null,
            isOrderDrawerOpen: viewState.isOrderDrawerOpen ?? false,
            orderDrawerPanelState: normalizeOrderDrawerPanelState(viewState.orderDrawerPanelState),
        };
    }, [location.state]);
    const homeReturnState = useMemo<HomeReturnState | null>(() => {
        const state = location.state as { homeReturnState?: HomeReturnState; } | null;
        if (!state?.homeReturnState) return null;

        return {
            ...state.homeReturnState,
            panelState: normalizeOrderDrawerPanelState(state.homeReturnState.panelState),
        };
    }, [location.state]);
    const { settings, updateSettings, tableSettingsSx } = useTableSettings('home');
    const { widths: colWidths, updateWidth: updateColWidth, resetWidths: resetColWidths } = useColumnWidths('home');
    const { tabConfig, updateTabConfig } = useTabConfig('home');
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const { showRightShadow } = useScrollShadow(scrollContainerRef);
    const { containerRef: toolbarRef, locationCompact, searchCompact } = useResponsiveToolbar();
    usePageTitle(session?.firstName ? `Welcome, ${session.firstName}` : 'Home', 'Home');

    const visibleFilterOptions = useMemo(() => {
        return tabConfig
            .filter(item => {
                if (!item.visible) return false;
                const tabDef = ALL_TABS.find(t => t.value === item.value);
                if (tabDef?.requiresRekeying && !session?.rekeyingEnabled) return false;
                return true;
            })
            .map(item => ALL_TABS.find(t => t.value === item.value)!)
            .filter(Boolean);
    }, [tabConfig, session?.rekeyingEnabled]);
    const cachedHomeState = useMemo(
        () => homeViewStateFromLocation ?? loadHomeViewStateCache(),
        [homeViewStateFromLocation]
    );
    const updateOrderDetails = useUpdateOrderDetails();
    const toast = useToast();
    const inlineComposeInputRef = useRef<HTMLTextAreaElement>(null);
    const [locationIds, setLocationIds] = useState<Array<string> | 'all'>(() => cachedHomeState?.locationIds ?? []);
    const [selectedOrderId, setSelectedOrderId] = useState<string | null>(() => homeReturnState?.selectedOrderId ?? cachedHomeState?.selectedOrderId ?? null);
    const [isOrderDrawerOpen, setIsOrderDrawerOpen] = useState(() => homeReturnState?.isOrderDrawerOpen ?? cachedHomeState?.isOrderDrawerOpen ?? false);
    const [disableOrderDrawerEnterAnimation, setDisableOrderDrawerEnterAnimation] = useState(() => Boolean(homeReturnState?.isOrderDrawerOpen));
    const [orderDrawerPanelState, setOrderDrawerPanelState] = useState<OrderDrawerPanelState>(() => (
        homeReturnState?.panelState
        ?? cachedHomeState?.orderDrawerPanelState
        ?? DEFAULT_ORDER_DRAWER_PANEL_STATE
    ));
    const [currentPage, setCurrentPage] = useState(() => cachedHomeState?.currentPage ?? 1);
    const [selectedFilter, setSelectedFilter] = useState<HomeFeedFilter | null>(() => {
        if (cachedHomeState) {
            return cachedHomeState.selectedFilter ?? null;
        }

        try {
            const saved = localStorage.getItem('home_feed_filters');
            if (!saved) return null;
            const parsed = JSON.parse(saved);
            // Backward compatibility: previous versions stored an array for multi-select.
            if (Array.isArray(parsed)) {
                return parsed[0] ?? null;
            }
            return parsed ?? null;
        } catch {
            return null;
        }
    });
    // The search comes back from a breadcrumb return (location state) or from
    // browser back/forward to this same history entry (in-memory), never from
    // the session cache: a refresh or sidebar click lands on an empty search.
    const [searchQuery, setSearchQuery] = useState(() => (
        homeViewStateFromLocation?.searchQuery ?? recallListSearch(location.key) ?? ''
    ));
    const [debouncedSearch, setDebouncedSearch] = useState(() => (
        homeViewStateFromLocation?.debouncedSearch
        ?? homeViewStateFromLocation?.searchQuery
        ?? recallListSearch(location.key)
        ?? ''
    ));
    // Set search and debounced search together so the debounce sees no local
    // edit and leaves the current page alone (only typing resets to page 1).
    const applySearch = useCallback((search: string, debounced: string = search) => {
        setSearchQuery(search);
        setDebouncedSearch(debounced);
    }, []);
    useRememberListSearch(searchQuery, applySearch);

    useEffect(() => {
        const state = location.state as { homeViewState?: Partial<HomeViewStateCache> } | null;
        const viewState = state?.homeViewState;
        if (!viewState) return;
        // Apply the handed-over search even when Home is already mounted: a
        // breadcrumb return carries the search that got the user to the detail,
        // while a sidebar click on the active tab hands over an empty one.
        const nextSearch = viewState.searchQuery ?? '';
        applySearch(nextSearch, viewState.debouncedSearch ?? nextSearch);
        // Consume the payload so a refresh does not replay the search.
        // `homeReturnState` stays so the order drawer can still reopen.
        const { homeViewState: _consumed, ...rest } = state;
        navigate(
            { pathname: location.pathname, search: location.search },
            { replace: true, state: rest },
        );
    }, [applySearch, location.pathname, location.search, location.state, navigate]);
    const [additionalFilters, setAdditionalFilters] = useState<AdditionalFilters>(() => {
        if (cachedHomeState) {
            return cachedHomeState.additionalFilters ?? {};
        }

        try {
            const saved = localStorage.getItem('home_advanced_filters');
            if (saved) {
                const parsed = JSON.parse(saved);
                // Convert date strings back to Date objects
                if (parsed.lastUpdatedRange) {
                    parsed.lastUpdatedRange = {
                        start: new Date(parsed.lastUpdatedRange.start),
                        end: new Date(parsed.lastUpdatedRange.end)
                    };
                }
                return parsed;
            }
            return {};
        } catch {
            return {};
        }
    });
    const searchChangeIsLocal = useRef(false);

    useEffect(() => {
        localStorage.setItem('home_feed_filters', JSON.stringify(selectedFilter));
    }, [selectedFilter]);
    const selectedFilters = useMemo<HomeFeedFilter[]>(
        () => (selectedFilter ? [selectedFilter] : []),
        [selectedFilter]
    );
    useEffect(() => {
        localStorage.setItem('home_advanced_filters', JSON.stringify(additionalFilters));
    }, [additionalFilters]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch((previous) => {
                if (previous === searchQuery) return previous;
                searchChangeIsLocal.current = true;
                return searchQuery;
            });
        }, 500);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        if (!searchChangeIsLocal.current) return;
        searchChangeIsLocal.current = false;
        setCurrentPage(1);
    }, [debouncedSearch]);

    useEffect(() => {
        if (!disableOrderDrawerEnterAnimation) return;

        const timerId = window.setTimeout(() => {
            setDisableOrderDrawerEnterAnimation(false);
        }, 0);

        return () => window.clearTimeout(timerId);
    }, [disableOrderDrawerEnterAnimation]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const cachedState: HomeViewStateCache = {
            locationIds,
            currentPage,
            selectedFilter,
            searchQuery,
            debouncedSearch,
            additionalFilters,
            selectedOrderId,
            isOrderDrawerOpen,
            orderDrawerPanelState,
        };

        sessionStorage.setItem(HOME_VIEW_STATE_CACHE_KEY, JSON.stringify(cachedState));
    }, [locationIds, currentPage, selectedFilter, searchQuery, debouncedSearch, additionalFilters, selectedOrderId, isOrderDrawerOpen, orderDrawerPanelState]);

    const limit = 20;
    const queryLimit = limit + 1;

    const {
        data,
        isLoading,
        isError,
        isRefetching,
        refetch
    } = useHomeFeedPaginated(
        locationIds,
        currentPage,
        queryLimit,
        selectedFilters,
        debouncedSearch,
        additionalFilters
    );

    const handleLocationChange = useCallback((newLocationIds: Array<string> | 'all') => {
        setLocationIds((previousLocationIds) => {
            if (areLocationSelectionsEqual(previousLocationIds, newLocationIds)) {
                return previousLocationIds;
            }
            setCurrentPage(1);
            return newLocationIds;
        });
    }, []);

    const handleFiltersChange = useCallback((newFilters: AdditionalFilters) => {
        setAdditionalFilters((previousFilters) => {
            if (areAdditionalFiltersEqual(previousFilters, newFilters)) {
                return previousFilters;
            }
            setCurrentPage(1);
            return newFilters;
        });
    }, []);

    // Inline compose popup state (for Send Message)
    const [inlineComposeOrderId, setInlineComposeOrderId] = useState<string | null>(null);
    const [inlineComposeText, setInlineComposeText] = useState('');
    const [inlineComposeMode, setInlineComposeMode] = useState<'message' | 'note'>('message');
    const [inlineComposePosition, setInlineComposePosition] = useState<{ top: number; left: number; } | null>(null);

    // @mention state for inline compose
    const [showMentionMenu, setShowMentionMenu] = useState(false);
    const [mentionFilter, setMentionFilter] = useState('');
    const [mentionCursorPos, setMentionCursorPos] = useState(0);
    const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
    const pendingMentionIds = useRef<string[]>([]);
    const { data: mentionableUsers } = useGetMentionableUsers(inlineComposeOrderId ?? '');

    const filteredMentionUsers = useMemo(() => {
        if (!mentionableUsers) return [];
        const others = mentionableUsers.filter((u: MentionableUser) => u._id !== session?.userId);
        if (!mentionFilter) return others;
        const lower = mentionFilter.toLowerCase();
        return others.filter((u: MentionableUser) =>
            u.displayName.toLowerCase().includes(lower)
        );
    }, [mentionableUsers, mentionFilter, session?.userId]);

    // Reset highlight index when the filtered list changes
    useEffect(() => {
        setSelectedMentionIndex(0);
    }, [mentionFilter]);

    const handleOrderClick = (orderId: string) => {
        setSelectedOrderId(orderId);
        setDisableOrderDrawerEnterAnimation(false);
        setOrderDrawerPanelState({
            ...DEFAULT_ORDER_DRAWER_PANEL_STATE,
            workflowTab: selectedFilter === 'supplier-changes' ? 'ordered' : DEFAULT_ORDER_DRAWER_PANEL_STATE.workflowTab,
        });
        setIsOrderDrawerOpen(true);
    };

    const handleOpenChat = (orderId: string, mode: 'message' | 'note', anchorEl?: HTMLElement | null) => {
        // Show inline compose popup below the StatusIndicator
        setInlineComposeOrderId(orderId);
        setInlineComposeText('');
        setInlineComposeMode(mode);
        if (anchorEl) {
            const rect = anchorEl.getBoundingClientRect();
            setInlineComposePosition({
                top: rect.bottom + 4,
                left: rect.left,
            });
        } else {
            setInlineComposePosition({ top: window.innerHeight / 2 - 100, left: window.innerWidth / 2 - 160 });
        }
        setTimeout(() => inlineComposeInputRef.current?.focus(), 100);
    };

    const dismissInlineCompose = () => {
        setInlineComposeOrderId(null);
        setInlineComposeText('');
        setInlineComposePosition(null);
        setShowMentionMenu(false);
        setMentionFilter('');
        pendingMentionIds.current = [];
    };

    const handleMentionSelect = useCallback((user: MentionableUser) => {
        const text = inlineComposeText;
        const beforeCursor = text.slice(0, mentionCursorPos);
        const atIndex = beforeCursor.lastIndexOf('@');
        if (atIndex === -1) return;

        const before = text.slice(0, atIndex);
        const after = text.slice(mentionCursorPos);
        const mention = `@${user.displayName} `;
        const newText = before + mention + after;

        if (!pendingMentionIds.current.includes(user._id)) {
            pendingMentionIds.current.push(user._id);
        }

        setInlineComposeText(newText);
        setShowMentionMenu(false);
        setMentionFilter('');
        inlineComposeInputRef.current?.focus();
    }, [inlineComposeText, mentionCursorPos]);

    const handleInlineComposeChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        const cursorPos = e.target.selectionStart || 0;
        setInlineComposeText(value);

        // @mentions only apply to messages, not private notes to self.
        if (inlineComposeMode === 'message') {
            const beforeCursor = value.slice(0, cursorPos);
            const atMatch = beforeCursor.match(/@(\w*)$/);
            if (atMatch) {
                setShowMentionMenu(true);
                setMentionFilter(atMatch[1]);
                setMentionCursorPos(cursorPos);
            } else {
                setShowMentionMenu(false);
                setMentionFilter('');
            }
        }
    };

    const handleInlineComposeSave = async () => {
        if (!inlineComposeText.trim() || !inlineComposeOrderId) return;
        const mentionedUserIds = inlineComposeMode === 'message' ? [...pendingMentionIds.current] : [];
        try {
            await updateOrderDetails.mutateAsync({
                orderId: inlineComposeOrderId,
                note: inlineComposeText.trim(),
                noteType: inlineComposeMode,
                ...(mentionedUserIds.length > 0 ? { mentionedUserIds } : {}),
            });
            dismissInlineCompose();
            refetch();
            toast({
                title: inlineComposeMode === 'message' ? 'Message sent' : 'Note added',
                status: 'success',
                duration: 2000,
                isClosable: true,
            });
        } catch {
            toast({
                title: inlineComposeMode === 'message' ? 'Failed to send message' : 'Failed to add note',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleInlineComposeKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Mention menu keyboard navigation
        if (showMentionMenu && filteredMentionUsers.length > 0) {
            if (e.key === 'Tab' || (e.key === 'Enter' && !e.metaKey && !e.ctrlKey)) {
                e.preventDefault();
                handleMentionSelect(filteredMentionUsers[selectedMentionIndex]);
                return;
            }
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedMentionIndex(prev =>
                    prev < filteredMentionUsers.length - 1 ? prev + 1 : 0
                );
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedMentionIndex(prev =>
                    prev > 0 ? prev - 1 : filteredMentionUsers.length - 1
                );
                return;
            }
        }

        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            handleInlineComposeSave();
        } else if (e.key === 'Escape') {
            if (showMentionMenu) {
                setShowMentionMenu(false);
            } else {
                dismissInlineCompose();
            }
        }
    };

    const formatDate = (date: Date) => {
        const orderDate = new Date(date);
        const today = new Date();

        const isToday = orderDate.toDateString() === today.toDateString();

        if (isToday) {
            return orderDate.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            }).toLowerCase();
        } else {
            return orderDate.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        }
    };

    const formatScheduledDate = (date: Date) => (
        new Date(date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        })
    );

    const isScheduledFilter = selectedFilter === 'scheduled';
    const isSupplierChangesFilter = selectedFilter === 'supplier-changes';

    const { orders, hasNextPage } = useMemo(() => {
        const fetched = data?.orders || [];
        return {
            orders: fetched.slice(0, limit),
            hasNextPage: fetched.length > limit,
        };
    }, [data?.orders, limit]);

    const pageOrderIds = useMemo(() => orders.map((order) => order._id), [orders]);
    const {
        selectedOrderIds,
        toggleOrderSelected,
        toggleSelectAllOnPage,
        allPageSelected,
        isPageSelectionIndeterminate,
        clearSelection,
    } = useOrderBulkSelection({ orderIds: pageOrderIds });
    const { runBulkAction, isLoading: isBulkLoading } = useOrderBulkActions(clearSelection);

    const bulkPermissions = useMemo(
        () => getOrderBulkPermissions(session?.permissions ?? []),
        [session?.permissions],
    );

    const selectedOrders = useMemo(
        () => orders
            .filter((order) => selectedOrderIds.has(order._id))
            .map((order) => ({
                _id: order._id,
                archived: order.archived,
                completed: order.completed,
                manuallyFlagged: order.manuallyFlagged,
                reviewedByCurrentUser: order.reviewedByCurrentUser,
                canComplete: order.canComplete,
            })),
        [orders, selectedOrderIds],
    );

    useEffect(() => {
        clearSelection();
    }, [currentPage, selectedFilter, debouncedSearch, locationIds, clearSelection]);

    const { scrollContainerProps: stickyCopyScrollProps } = useStickyTableCopyFront(
        scrollContainerRef,
        `${isLoading}-${orders.length}-${selectedFilter}`,
    );

    const totalPages = useMemo(() => {
        return hasNextPage ? currentPage + 1 : currentPage;
    }, [hasNextPage, currentPage]);

    if (session?.isAuthenticated && !session.loading && !session.tabs.includes('ro-manager')) {
        return <Navigate to="/activity" replace />;
    }

    return (
        <Box
            w="calc(100% + 2rem)"
            mx={-4}
            mt={-4}
            mb={-2}
            pb={0}
            h="calc(100dvh - var(--partbay-topbar))"
            display="flex"
            flexDirection="column"
            overflow='hidden'
        >
            {/* Row 1 — status tabs + toolbar actions, hairline underneath (ENG-2297). */}
            <ListTabRow
                actions={
                    <HStack spacing={1} flexShrink={0}>
                        <TableSettingsButton
                            settings={settings}
                            onUpdateSettings={updateSettings}
                            hasCustomColumnWidths={Object.keys(colWidths).length > 0}
                            onResetColumnWidths={resetColWidths}
                            tabConfig={tabConfig}
                            onUpdateTabConfig={updateTabConfig}
                            rekeyingEnabled={session?.rekeyingEnabled ?? undefined}
                        />
                        <RefreshButton
                            refetch={refetch}
                            isLoading={isRefetching}
                        />
                    </HStack>
                }
            >
                    <Button
                        size="sm"
                        h="32px"
                        px={4}
                        borderRadius="full"
                        fontWeight="medium"
                        fontSize="sm"
                        flexShrink={0}
                        bg={!selectedFilter ? "#0071E3" : "transparent"}
                        color={!selectedFilter ? "white" : "gray.600"}
                        _hover={{
                            bg: !selectedFilter ? "#0071E3" : "gray.100",
                        }}
                        _active={{
                            bg: !selectedFilter ? "#0071E3" : "gray.100",
                        }}
                        onClick={() => {
                            setSelectedFilter(null);
                            setCurrentPage(1);
                        }}
                        leftIcon={<Icon as={FiList as React.ElementType} boxSize={3.5} />}
                    >
                        All
                    </Button>
                    {visibleFilterOptions.map((filter) => {
                        const isSelected = selectedFilter === filter.value;
                        return (
                            <Button
                                key={filter.value}
                                size="sm"
                                h="32px"
                                px={4}
                                borderRadius="full"
                                fontWeight="medium"
                                fontSize="sm"
                                flexShrink={0}
                                bg={isSelected ? "#0071E3" : "transparent"}
                                color={isSelected ? "white" : "gray.600"}
                                _hover={{
                                    bg: isSelected ? "#0071E3" : "gray.100",
                                }}
                                _active={{
                                    bg: isSelected ? "#0071E3" : "gray.100",
                                }}
                                onClick={() => {
                                    setSelectedFilter(isSelected ? null : filter.value);
                                    setCurrentPage(1);
                                }}
                                leftIcon={<Icon as={filter.icon as React.ElementType} boxSize={3.5} />}
                            >
                                {filter.label}
                            </Button>
                        );
                    })}
            </ListTabRow>

            {/* Row 2 — search + filters left, location far right (ENG-2297 #4). */}
            <Flex
                ref={toolbarRef}
                my={3}
                px={6}
                flexShrink={0}
                align="center"
                gap={2}
            >
                <CollapsibleSearch
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by VIN or RO#..."
                    isCompact={searchCompact}
                    width={LIST_PAGE_SEARCH_WIDTH}
                />
                <AdvancedFilters
                    filters={additionalFilters}
                    onFiltersChange={handleFiltersChange}
                />
                <Box ml="auto" flexShrink={0}>
                    <LocationSelector
                        storageKey="location_ids"
                        onLocationChange={handleLocationChange}
                        width="260px"
                        icon={TbBuilding}
                        iconOnly={locationCompact}
                    />
                </Box>
            </Flex>

            <Box
                flex={1}
                display="flex"
                flexDirection="column"
                overflow="hidden"
                minH={0}
            >
                <Box position="relative" flex={1} minH={0} display="flex" flexDirection="column">
                    <Box
                        ref={scrollContainerRef}
                        {...stickyCopyScrollProps}
                        flex={1}
                        minH={0}
                        overflow="auto"
                        borderTop="1px solid"
                        borderColor="gray.200"
                        sx={{
                            ...tableSettingsSx,
                            overscrollBehavior: 'contain',
                            '&::-webkit-scrollbar': {
                                width: '8px',
                                height: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                                background: 'transparent',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: '#cbd5e0',
                                borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-thumb:hover': {
                                background: '#a0aec0',
                            },
                            scrollbarWidth: 'thin',
                            scrollbarColor: '#cbd5e0 transparent',
                            '& table': {
                                width: 'max-content !important',
                                minWidth: '100%',
                            },
                            ...stickyTableScrollCopySx,
                            '& th': {
                                ...(tableSettingsSx['& th'] || {}),
                                borderBottomWidth: '0 !important',
                                boxShadow: 'inset 0 -1px 0 0 var(--chakra-colors-gray-200)',
                                bg: 'gray.50',
                            },
                            '& th:first-of-type': {
                                position: 'sticky',
                                left: 0,
                                bg: 'gray.50',
                                zIndex: 3,
                            },
                            '& td:first-of-type': {
                                position: 'sticky',
                                left: 0,
                                zIndex: 2,
                                background: 'inherit',
                            },
                            '& tr:hover .order-row-checkbox': {
                                opacity: '1 !important',
                                pointerEvents: 'auto !important',
                            },
                            '& thead tr:hover .order-select-all-checkbox': {
                                opacity: '1 !important',
                                pointerEvents: 'auto !important',
                            },
                        }}
                    >
                        {isLoading ? (
                            <Flex justify="center" align="center" h="400px">
                                <Spinner size="xl" color="blue.500" />
                            </Flex>
                        ) : isError ? (
                            <Box p={8}>
                                <Alert status="error">
                                    <AlertIcon />
                                    Failed to load home feed.
                                </Alert>
                            </Box>
                        ) : (
                            <>
                                <Table variant="simple" size="sm" minW="100%" sx={Object.keys(colWidths).length > 0 ? { tableLayout: 'fixed', '& td': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, '& td:last-child': { overflow: 'visible', textOverflow: 'unset', whiteSpace: 'nowrap' } } : { '& td:last-child': { whiteSpace: 'nowrap' } }}>
                                    <Thead position="sticky" top={0} zIndex={STICKY_TABLE_HEADER_Z_INDEX}>
                                        <Tr bg="gray.50">
                                            <ResizableTh columnKey="roNumber" storedWidth={colWidths.roNumber} onResize={updateColWidth} minWidth={140} color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" py={2.5} pl={HOME_CONTENT_PL_WITH_BULK} pr={3} position="relative" textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200">
                                                <OrderSelectAllHeaderCheckbox
                                                    contentPlWithBulk={HOME_CONTENT_PL_WITH_BULK}
                                                    allPageSelected={allPageSelected}
                                                    isPageSelectionIndeterminate={isPageSelectionIndeterminate}
                                                    onToggle={toggleSelectAllOnPage}
                                                    isDisabled={orders.length === 0}
                                                    ariaLabel="Select all orders on this page"
                                                />
                                                RO Number
                                            </ResizableTh>
                                            <ResizableTh columnKey="vehicle" storedWidth={colWidths.vehicle} onResize={updateColWidth} minWidth={120} color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" py={2.5} px={3} textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200">Vehicle</ResizableTh>
                                            {isSupplierChangesFilter && <ResizableTh columnKey="partChanges" storedWidth={colWidths.partChanges} onResize={updateColWidth} minWidth={110} color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" py={2.5} px={3} textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200">Part Changes</ResizableTh>}
                                            <ResizableTh columnKey="location" storedWidth={colWidths.location} onResize={updateColWidth} minWidth={100} color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" py={2.5} px={3} textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200">Location</ResizableTh>
                                            <ResizableTh columnKey="insurance" storedWidth={colWidths.insurance} onResize={updateColWidth} minWidth={100} color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" py={2.5} px={3} textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200">Insurance</ResizableTh>
                                            <ResizableTh columnKey="lastUpdated" storedWidth={colWidths.lastUpdated} onResize={updateColWidth} minWidth={110} color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" py={2.5} px={3} textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200">Last Updated</ResizableTh>
                                            {isScheduledFilter && <ResizableTh columnKey="scheduledFor" storedWidth={colWidths.scheduledFor} onResize={updateColWidth} minWidth={140} color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" py={2.5} px={3} textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200">Scheduled For</ResizableTh>}
                                            <ResizableTh columnKey="daysActive" storedWidth={colWidths.daysActive} onResize={updateColWidth} minWidth={90} color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" py={2.5} px={3} textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200">Days Active</ResizableTh>
                                            <ResizableTh columnKey="parts" storedWidth={colWidths.parts} onResize={updateColWidth} minWidth={70} color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" py={2.5} px={3} isNumeric textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200">Parts</ResizableTh>
                                            <ResizableTh columnKey="grossMargin" storedWidth={colWidths.grossMargin} onResize={updateColWidth} minWidth={100} color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" py={2.5} px={3} isNumeric textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200">Gross Margin</ResizableTh>
                                            {!selectedFilters.includes('completed') && <Th py={2.5} pl={3} pr={6} w="auto" minW="120px" borderBottomWidth="1px" borderColor="gray.200" />}
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {orders.map((order, rowIndex) => {
                                            const isSelected = selectedOrderIds.has(order._id);
                                            const isReviewed = order.reviewedByCount > 0;
                                            const isSaved = order.manuallyFlagged;
                                            const rowBg = isSelected
                                                ? 'blue.50'
                                                : isReviewed
                                                    ? '#E9E1F5'
                                                    : isSaved
                                                        ? 'orange.50'
                                                        : 'white';
                                            const rowHoverBg = isSelected
                                                ? 'blue.100'
                                                : isReviewed
                                                    ? '#DDD1F0'
                                                    : isSaved
                                                        ? 'orange.100'
                                                        : 'gray.50';

                                            return (
                                                <Tr
                                                    key={order._id}
                                                    bg={rowBg}
                                                    _hover={{ bg: rowHoverBg, cursor: 'pointer' }}
                                                    onClick={(e) => handleOrderBulkRowClick(
                                                        e,
                                                        order._id,
                                                        rowIndex,
                                                        toggleOrderSelected,
                                                        () => handleOrderClick(order._id),
                                                        true,
                                                    )}
                                                >
                                                    <Td py={3} pl={HOME_CONTENT_PL_WITH_BULK} pr={3} position="relative" borderBottomWidth="1px" borderColor="gray.100">
                                                        <HomeRoColumnRow
                                                            checkbox={(
                                                                <OrderRoColumnCheckboxSlot
                                                                    orderId={order._id}
                                                                    isChecked={isSelected}
                                                                    rowIndex={rowIndex}
                                                                    onToggle={toggleOrderSelected}
                                                                />
                                                            )}
                                                            status={(
                                                                <StatusIndicator
                                                                    orderId={order._id}
                                                                    completed={order.completed}
                                                                    manuallyFlagged={order.manuallyFlagged}
                                                                    reviewedByCurrentUser={order.reviewedByCurrentUser}
                                                                    onToggleReview={(id, reviewed) => {
                                                                        updateOrderDetails.mutate({ orderId: id, reviewedByMe: reviewed });
                                                                    }}
                                                                    iconSize="16px"
                                                                    onOpenChat={handleOpenChat}
                                                                />
                                                            )}
                                                        >
                                                            <ChakraLink
                                                                as={RouterLink}
                                                                to={`/order/${order._id}`}
                                                                state={{
                                                                    source: 'home',
                                                                    ...(selectedFilter === 'supplier-changes' ? { initialStatus: 'ordered' as const } : {}),
                                                                }}
                                                                onClick={(e) => e.stopPropagation()}
                                                                color="blue.600"
                                                                fontWeight="medium"
                                                                fontSize="sm"
                                                                _hover={{ textDecoration: 'underline' }}
                                                            >
                                                                {order.roNumber}
                                                            </ChakraLink>
                                                            <NoteCountBadge noteCount={order.noteCount} orderId={order._id} />
                                                            <SupplierCommunicationCountBadge count={order.supplierCommunicationCount} />
                                                        </HomeRoColumnRow>
                                                    </Td>
                                                    <Td py={3} px={3} borderBottomWidth="1px" borderColor="gray.100">
                                                        <VStack align="flex-start" spacing={0}>
                                                            <Text fontSize="sm" fontWeight="medium" color="gray.800">
                                                                {order.vehicle}
                                                            </Text>
                                                            <Vin vin={order.vin} />
                                                        </VStack>
                                                    </Td>
                                                    {isSupplierChangesFilter && (
                                                        <Td py={3} px={3} borderBottomWidth="1px" borderColor="gray.100">
                                                            {order.supplierChangesCount > 0 ? (
                                                                <Flex
                                                                    display="inline-flex"
                                                                    align="center"
                                                                    justify="center"
                                                                    minW="22px"
                                                                    h="22px"
                                                                    px={2}
                                                                    borderRadius="full"
                                                                    bg="orange.100"
                                                                    color="orange.700"
                                                                >
                                                                    <Text fontSize="xs" fontWeight="bold" lineHeight="1">
                                                                        {order.supplierChangesCount}
                                                                    </Text>
                                                                </Flex>
                                                            ) : (
                                                                <Text fontSize="sm" color="gray.400">—</Text>
                                                            )}
                                                        </Td>
                                                    )}
                                                    <Td py={3} px={3} borderBottomWidth="1px" borderColor="gray.100">
                                                        <Text fontSize="sm" color="gray.700">
                                                            {order.location}
                                                        </Text>
                                                    </Td>
                                                    <Td py={3} px={3} borderBottomWidth="1px" borderColor="gray.100">
                                                        <Text fontSize="sm" color="gray.700">
                                                            {order.insurance ?? '—'}
                                                        </Text>
                                                    </Td>
                                                    <Td py={3} px={3} borderBottomWidth="1px" borderColor="gray.100">
                                                        <Text fontSize="sm" color="gray.700">
                                                            {formatDate(order.lastUpdated)}
                                                        </Text>
                                                    </Td>
                                                    {isScheduledFilter && (
                                                        <Td py={3} px={3} borderBottomWidth="1px" borderColor="gray.100">
                                                            <Text fontSize="sm" color="gray.700">
                                                                {order.scheduledDate ? formatScheduledDate(order.scheduledDate) : '—'}
                                                            </Text>
                                                        </Td>
                                                    )}
                                                    <Td py={3} px={3} borderBottomWidth="1px" borderColor="gray.100">
                                                        <Text fontSize="sm" color="gray.700">
                                                            {order.daysActive}
                                                        </Text>
                                                    </Td>
                                                    <Td py={3} px={3} isNumeric borderBottomWidth="1px" borderColor="gray.100">
                                                        <Text fontSize="sm" color="gray.700">
                                                            {order.partsOrdered} / {order.totalParts}
                                                        </Text>
                                                    </Td>
                                                    <Td py={3} px={3} isNumeric borderBottomWidth="1px" borderColor="gray.100">
                                                        <Text fontSize="sm" color="gray.700">
                                                            {order.grossMargin !== null ? formatPercentage(order.grossMargin) : '-'}
                                                        </Text>
                                                    </Td>
                                                    {!selectedFilters.includes('completed') && <Td py={3} pl={3} pr={6} w="auto" minW="120px" borderBottomWidth="1px" borderColor="gray.100" overflow="visible" whiteSpace="nowrap">
                                                        <HStack gap={1} w="auto" justify="flex-end" flexWrap="nowrap">
                                                            {order.actions.map(a => (
                                                                <ActionBadge
                                                                    key={a.key}
                                                                    action={a.key}
                                                                    total={a.total}
                                                                    orderId={order._id}
                                                                    expanded={false}
                                                                    exclamation={a.exclamation}
                                                                    flag={a.flag}
                                                                    source='home'
                                                                />
                                                            ))}
                                                        </HStack>
                                                    </Td>}
                                                </Tr>
                                            );
                                        })}
                                    </Tbody>
                                </Table>
                            </>
                        )}
                    </Box>
                    {showRightShadow && (
                        <Box
                            position="absolute"
                            right={0}
                            top={0}
                            bottom={0}
                            w="48px"
                            bgGradient="linear(to-r, rgba(255,255,255,0), rgba(255,255,255,0.85))"
                            pointerEvents="none"
                            zIndex={4}
                        />
                    )}
                    {selectedOrderIds.size > 0 && (
                        <OrderBulkActionBar
                            selectedOrders={selectedOrders}
                            permissions={bulkPermissions}
                            onClearSelection={clearSelection}
                            onAction={(actionId) => void runBulkAction(actionId, Array.from(selectedOrderIds))}
                            isLoading={isBulkLoading}
                        />
                    )}
                </Box>

                <Box flexShrink={0} borderTop="1px solid" borderColor="gray.200">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        isLoading={isLoading || isRefetching}
                        hasNextPage={hasNextPage}
                    />
                </Box>
            </Box>
            <OrderDrawer
                isOpen={isOrderDrawerOpen}
                onClose={() => {
                    setIsOrderDrawerOpen(false);
                    setSelectedOrderId(null);
                    setDisableOrderDrawerEnterAnimation(false);
                    setOrderDrawerPanelState(DEFAULT_ORDER_DRAWER_PANEL_STATE);
                }}
                orderId={selectedOrderId}
                initialPanelState={orderDrawerPanelState}
                disableEnterAnimation={disableOrderDrawerEnterAnimation}
                onPanelStateChange={(panelState) => {
                    setOrderDrawerPanelState((previousState) => (
                        areOrderDrawerPanelStatesEqual(previousState, panelState)
                            ? previousState
                            : panelState
                    ));
                }}
            />

            {/* Inline Compose Popup (positioned below StatusIndicator) */}
            {inlineComposeOrderId && inlineComposePosition && (
                <Portal>
                    {/* Click-away backdrop */}
                    <Box
                        position="fixed"
                        inset={0}
                        zIndex={1400}
                        onClick={dismissInlineCompose}
                    />
                    {/* Compose popup */}
                    <Box
                        position="fixed"
                        top={`${inlineComposePosition.top}px`}
                        left={`${inlineComposePosition.left}px`}
                        zIndex={1500}
                        bg={inlineComposeMode === 'note' ? note.paper : 'white'}
                        borderWidth="1px"
                        borderColor={inlineComposeMode === 'note' ? note.border : 'gray.200'}
                        borderRadius="xl"
                        boxShadow="none"
                        w="320px"
                        p={4}
                    >
                        <HStack spacing={2} mb={3}>
                            <Icon
                                as={(inlineComposeMode === 'note' ? TbPinnedFilled : TbMessageCircle) as React.ElementType}
                                w={4}
                                h={4}
                                color={inlineComposeMode === 'note' ? note.ink : '#0071E3'}
                            />
                            <Text
                                fontSize="sm"
                                fontWeight="semibold"
                                color={inlineComposeMode === 'note' ? note.ink : 'gray.700'}
                            >
                                {inlineComposeMode === 'note' ? 'Note to self' : 'Send Message'}
                            </Text>
                        </HStack>
                        <Box position="relative">
                            {/* @mention dropdown */}
                            {showMentionMenu && inlineComposeMode === 'message' && filteredMentionUsers.length > 0 && (
                                <Box
                                    position="absolute"
                                    bottom="100%"
                                    left={0}
                                    right={0}
                                    mb={1}
                                    bg="white"
                                    borderWidth="1px"
                                    borderColor="gray.200"
                                    borderRadius="lg"
                                    boxShadow="none"
                                    maxH="160px"
                                    overflow="auto"
                                    zIndex={10}
                                >
                                    {filteredMentionUsers.map((user: MentionableUser, index: number) => (
                                        <Box
                                            key={user._id}
                                            px={3}
                                            py={2}
                                            cursor="pointer"
                                            bg={index === selectedMentionIndex ? 'gray.100' : undefined}
                                            _hover={{ bg: 'gray.100' }}
                                            onClick={() => handleMentionSelect(user)}
                                            ref={index === selectedMentionIndex ? (el) => el?.scrollIntoView({ block: 'nearest' }) : undefined}
                                        >
                                            <HStack spacing={2} justify="space-between">
                                                <Box>
                                                    <Text fontSize="sm" fontWeight="medium" color="gray.800">
                                                        {user.displayName}
                                                    </Text>
                                                    <Text fontSize="xs" color="gray.500">
                                                        {positionLabel(user.role, user.position)}
                                                    </Text>
                                                </Box>
                                                {index === selectedMentionIndex && (
                                                    <Text fontSize="xs" color="gray.400" flexShrink={0}>
                                                        tab
                                                    </Text>
                                                )}
                                            </HStack>
                                        </Box>
                                    ))}
                                </Box>
                            )}

                            <Textarea
                                ref={inlineComposeInputRef}
                                as={ResizeTextarea}
                                placeholder={inlineComposeMode === 'note' ? 'Write a note to self...' : 'Type a message...'}
                                value={inlineComposeText}
                                onChange={handleInlineComposeChange}
                                onKeyDown={handleInlineComposeKeyDown}
                                resize="none"
                                minH="80px"
                                maxRows={6}
                                fontSize="sm"
                                borderRadius="lg"
                                borderColor={inlineComposeMode === 'note' ? note.border : 'gray.300'}
                                bg="white"
                                pr="48px"
                                pb="36px"
                                overflow="auto"
                                isDisabled={updateOrderDetails.isPending}
                                _placeholder={{ color: 'gray.400' }}
                                _hover={{ borderColor: inlineComposeMode === 'note' ? note.deep : 'gray.400' }}
                                _focus={{
                                    borderColor: inlineComposeMode === 'note' ? note.deep : '#0071E3',
                                    boxShadow: 'none',
                                    bg: 'white',
                                }}
                            />
                            <IconButton
                                aria-label={inlineComposeMode === 'note' ? 'Save note' : 'Send message'}
                                icon={<Icon as={TbArrowUp as React.ElementType} w={4} h={4} />}
                                position="absolute"
                                bottom="8px"
                                right="8px"
                                size="sm"
                                borderRadius="lg"
                                bg={inlineComposeMode === 'note' ? note.ink : '#0071E3'}
                                color="white"
                                _hover={{ bg: inlineComposeMode === 'note' ? note.inkHover : '#3544a0' }}
                                _disabled={{
                                    bg: inlineComposeMode === 'note' ? note.ink : '#0071E3',
                                    opacity: 0.6,
                                    cursor: 'not-allowed',
                                }}
                                onClick={handleInlineComposeSave}
                                isLoading={updateOrderDetails.isPending}
                                isDisabled={!inlineComposeText.trim()}
                                minW="28px"
                                h="28px"
                            />
                        </Box>
                        <Text fontSize="xs" color={inlineComposeMode === 'note' ? note.muted : 'gray.400'} mt={1.5} ml={1}>
                            {inlineComposeMode === 'note' ? 'Press Ctrl+Enter to save • Esc to cancel' : 'Press Ctrl+Enter to send • Type @ to mention'}
                        </Text>
                    </Box>
                </Portal>
            )}
        </Box>
    );
};

export default Home;